/**
 * Circular score dial for the Genre Mixer's compatibility readout.
 *
 * Geometry, stroke widths, the -90° rotation and the counter-rotated centre label
 * are verbatim. Two changes, neither visual:
 *
 *   - The `c` theme object was passed in as a prop; it is imported directly now.
 *   - The SVG had no accessible name, so the score was invisible to assistive
 *     tech even though it is the panel's headline number.
 */
import { c } from '../../styles/theme';

export interface CompatibilityRingProps {
  /** 0–100. */
  score: number;
}

export default function CompatibilityRing({ score }: CompatibilityRingProps) {
  const size = 64;
  const stroke = 6;
  const r = (size - stroke) / 2;
  const circumference = 2 * Math.PI * r;
  const filled = (score / 100) * circumference;

  return (
    <svg
      width={size}
      height={size}
      role="img"
      aria-label={`Genre compatibility ${score} out of 100`}
      style={{ flexShrink: 0, transform: 'rotate(-90deg)' }}
    >
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        stroke={c.surface3}
        strokeWidth={stroke}
        fill="none"
      />
      <circle
        cx={size / 2}
        cy={size / 2}
        r={r}
        stroke={c.accent}
        strokeWidth={stroke}
        fill="none"
        strokeDasharray={`${filled} ${circumference}`}
        strokeLinecap="round"
        style={{ transition: 'stroke-dasharray 0.6s ease' }}
      />
      <text
        x={size / 2}
        y={size / 2}
        textAnchor="middle"
        dominantBaseline="middle"
        fill={c.text1}
        fontSize="14"
        fontWeight="600"
        transform={`rotate(90 ${size / 2} ${size / 2})`}
      >
        {score}
      </text>
    </svg>
  );
}
